/* ⚠️ GENERATED from app/services/postTags.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   🏷 postTags.ts — tagging people in a post. One rule for the server (which
   decides and notifies) and both clients (which ask and draw the line).

   THE SHAPE ON THE POST
     taggedUids   [uid]          who the AUTHOR asked to tag (the author writes it)
     tagNames     {uid: name}    the names as the author saw them (for drawing)
     tagStatus    {uid: status}  the server's answer per person (server-owned)
     tagsApproved [uid]          who is shown — the only list anybody draws, and
                                 the one a profile's "Tagged" tab asks for
                                 (server-owned, so nobody can put themselves on
                                 somebody else's page)

   WHO MAY BE TAGGED — privacySettings on the person being tagged
     tagging   'everyone' (absent) | 'friends' | 'nobody'
     tagReview true → every tag waits for their yes; absent / false → shown at once
   A block in EITHER direction refuses the tag silently: a tag must not be a way
   to reach somebody who blocked you, nor to drag somebody you blocked onto a post.
   ═══════════════════════════════════════════════════════════════════════════ */

export const TAG_MAX = 20;
export const TAGGING_OPTIONS = ['everyone', 'friends', 'nobody'];

/** The setting as the server reads it — anything unknown is the default. */
export function taggingSetting(user) {
  const v = user && user.privacySettings && user.privacySettings.tagging;
  return v === 'friends' || v === 'nobody' ? v : 'everyone';
}

/** Absent is off: a tag shows at once unless the person asked to review. */
export function reviewsTags(user) {
  return !!(user && user.privacySettings && user.privacySettings.tagReview === true);
}

/** What the author asked for, cleaned: strings, no duplicates, never themselves, at most TAG_MAX. */
export function cleanTagRequest(uids, authorUid) {
  const out = [];
  const seen = new Set();
  for (const u of Array.isArray(uids) ? uids : []) {
    const id = typeof u === 'string' ? u.trim() : '';
    if (!id || id === authorUid || seen.has(id) || id.length > 128) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= TAG_MAX) break;
  }
  return out;
}
/** The server's answer for one person. */
export function decideTag(i) {
  const t = i.target;
  if (!t || t.isGuest === true || t.deleted === true || t.disabled === true) return 'refused';
  if (i.blocked) return 'refused';
  const s = taggingSetting(t);
  if (s === 'nobody') return 'refused';
  if (s === 'friends' && !i.isFriend) return 'refused';
  return reviewsTags(t) ? 'pending' : 'approved';
}

/**
 * The work an announce has to do: who is new (and what they get), and who the
 * author took off since. A person with ANY status already is never decided
 * again — so a decline or a self-removal is not undone by the author saving
 * the post a second time.
 */
export function planAnnounce(post, authorUid) {
  const want = cleanTagRequest(post && post.taggedUids, authorUid);
  const status = (post ? post.tagStatus : null) || {};
  const fresh = want.filter(u => !status[u]);
  const dropped = Object.keys(status).filter(u => want.indexOf(u) < 0 && (status[u] === 'approved' || status[u] === 'pending'));
  return {
    fresh,
    dropped
  };
}

/** The people a post shows as "with …", in the order the author tagged them. */
export function shownTags(post) {
  const approved = Array.isArray(post && post.tagsApproved) ? post.tagsApproved.map(String) : [];
  if (!approved.length) return [];
  const order = Array.isArray(post && post.taggedUids) ? post.taggedUids.map(String) : [];
  const names = (post ? post.tagNames : null) || {};
  const ranked = order.filter(u => approved.indexOf(u) >= 0).concat(approved.filter(u => order.indexOf(u) < 0));
  return ranked.map(uid => ({
    uid,
    name: String(names[uid] || '').trim() || '…'
  }));
}

/** "with A", "with A and B", "with A and 3 others" — the pieces, so each client can make names tappable. */
export function tagLine(post, liveName) {
  const all = shownTags(post).map(p => {
    const n = liveName ? liveName(p.uid) : null;
    return n ? {
      uid: p.uid,
      name: String(n)
    } : p;
  });
  if (all.length <= 2) return {
    named: all,
    others: 0
  };
  return {
    named: all.slice(0, 1),
    others: all.length - 1
  };
}

/** Is this person on the post as a tag that shows? */
export function isTaggedIn(post, uid) {
  if (!uid || !post) return false;
  return Array.isArray(post.tagsApproved) && post.tagsApproved.map(String).indexOf(String(uid)) >= 0;
}

/** May this viewer open a friends-only post? Author, the author's friends, anyone tagged in it, and anyone asked to be. */
export function mayOpenFriendsPost(post, viewer, isFriend) {
  if (!post || post.audience !== 'friends') return true;
  if (!viewer) return false;
  if (String(post.userId) === String(viewer)) return true;
  if (isFriend(String(post.userId))) return true;
  if (isTaggedIn(post, viewer)) return true;
  /* asked to be tagged: they must be able to see what they are deciding about */
  return !!(post.tagStatus && post.tagStatus[String(viewer)] === 'pending');
}
